import React from "react";
import { Box, Paper, Typography, Skeleton } from "@mui/material";

import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VerifiedIcon from "@mui/icons-material/Verified";
import AnalyticsIcon from "@mui/icons-material/Analytics";

const cardStyle = {
  p: 1,
  minWidth: "24%",
  position: "relative",
  overflow: "hidden",
  borderRadius: 3,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  background: "linear-gradient(135deg,#ffffff 0%,#f5f8ff 100%)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  transition: "all 0.3s ease",
  //   "&:hover": {
  //     transform: "translateY(-4px)",
  //     boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
  //   },
};

function StatItem({ label, value, icon, color = "#333", onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        pb: 0.2,
        cursor: onClick ? "pointer" : "default",
        borderRadius: 1,
        px: 0.5,
        "&:hover": onClick
          ? {
              backgroundColor: "rgba(0,0,0,0.05)",
            }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {icon}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const categoryConfig = {
  Incident: {
    icon: <AssignmentIcon color="error" />,
    gradient: "linear-gradient(135deg,#fff7f7 0%,#ffe5e5 100%)",
    watermark: AssignmentIcon,
  },
  Service: {
    icon: <PendingActionsIcon color="warning" />,
    gradient: "linear-gradient(135deg,#fffaf1 0%,#ffecc7 100%)",
    watermark: PendingActionsIcon,
  },
  Request: {
    icon: <CheckCircleIcon color="success" />,
    gradient: "linear-gradient(135deg,#f4fff8 0%,#d9fbe8 100%)",
    watermark: CheckCircleIcon,
  },
};

function KpiCardsSkeleton() {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "auto",
        justifyContent: "space-between",
        pb: 1,
      }}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            ...cardStyle,
            minWidth: "24%",
            background: "linear-gradient(135deg,#ffffff 0%,#f5f8ff 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width="60%" height={24} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: 3 }).map((__, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={80} height={18} />
                </Box>
                <Skeleton variant="text" width={28} height={18} />
              </Box>
            ))}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

export default function KpiCards({ kpis, onStatusFilter, loading = false }) {
  if (loading) return <KpiCardsSkeleton />;
  if (!kpis) return null;

  const categoryKeys = Object.keys(kpis).filter(
    (k) => kpis[k] && typeof kpis[k] === "object",
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "auto",
        justifyContent: "space-between",
        pb: 1,
      }}
    >
      {categoryKeys.map((key) => {
        const data = kpis[key] || {};

        const config = categoryConfig[key] || {
          icon: <AnalyticsIcon color="primary" />,
          gradient: "linear-gradient(135deg,#ffffff 0%,#f5f8ff 100%)",
          watermark: AnalyticsIcon,
        };

        const WatermarkIcon = config.watermark;

        return (
          <Paper
            key={key}
            elevation={0}
            sx={{
              ...cardStyle,
              background: config.gradient,
            }}
          >
            {/* Background Watermark */}
            <Box
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                opacity: 0.08,
                transform: "rotate(-20deg)",
                pointerEvents: "none",
              }}
            >
              <WatermarkIcon sx={{ fontSize: 100 }} />
            </Box>

            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              {config.icon}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                }}
              >
                {key}
              </Typography>
            </Box>

            {/* Stats */}
            <Box>
              <StatItem
                label="Open"
                value={data.open ?? 0}
                color="#ef4444"
                onClick={() =>
                  onStatusFilter?.({
                    category: key,
                    status: "New",
                  })
                }
                icon={
                  <AssignmentIcon
                    sx={{
                      fontSize: 16,
                      color: "#ef4444",
                    }}
                  />
                }
              />

              <StatItem
                label="In Progress"
                value={data.inprogress ?? 0}
                color="#f59e0b"
                onClick={() =>
                  onStatusFilter?.({
                    category: key,
                    status: "Work in progress",
                  })
                }
                icon={
                  <PendingActionsIcon
                    sx={{
                      fontSize: 16,
                      color: "#f59e0b",
                    }}
                  />
                }
              />

              <StatItem
                label="Closed"
                value={data.closed ?? 0}
                color="#22c55e"
                onClick={() =>
                  onStatusFilter?.({
                    category: key,
                    status: "Closed",
                  })
                }
                icon={
                  <CheckCircleIcon
                    sx={{
                      fontSize: 16,
                      color: "#22c55e",
                    }}
                  />
                }
              />
            </Box>
          </Paper>
        );
      })}

      {/* Performance KPI Card */}
      <Paper
        elevation={0}
        sx={{
          ...cardStyle,
          background: "linear-gradient(135deg,#f3f8ff 0%,#dbeafe 100%)",
          color: "#fff",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -15,
            right: -15,
            opacity: 0.12,
            pointerEvents: "none",
          }}
        >
          <VerifiedIcon sx={{ fontSize: 110 }} />
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            // mb: 0.5,
            color: "#000",
          }}
        >
          Performance
        </Typography>

        <StatItem
          label="FTR"
          value={kpis.FTR ?? 0}
          color="#000"
          icon={
            <SpeedIcon
              sx={{
                fontSize: 16,
                color: "#93c5fd",
              }}
            />
          }
        />

        <StatItem
          label="OTIF"
          value={kpis.OTIF ?? 0}
          color="#000"
          icon={
            <LocalShippingIcon
              sx={{
                fontSize: 16,
                color: "#86efac",
              }}
            />
          }
        />

        <StatItem
          label="SLA"
          value={kpis.SLA ?? 0}
          color="#000"
          icon={
            <VerifiedIcon
              sx={{
                fontSize: 16,
                color: "#d8b4fe",
              }}
            />
          }
        />
      </Paper>
    </Box>
  );
}
